import CloseIcon from '@mui/icons-material/Close'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import find from 'lodash/find'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'

import { useGetAccountMetaQuery } from 'api/meta/getAccountMeta'
import { HHAlert } from 'components/common/HHAlert'
import HHConfirmDialog from 'components/common/HHConfirmDialog'
import { DS_CAPTION_SX, DS_TITLE_SX } from 'components/taxes/common/dsTypography'
import T from 'T'
import { Z_INDEX } from 'theme/zIndex'
import { get } from 'utils/lodash'
import { usePreviousValue } from 'utils/react'

import { NEW_ACCOUNT_INITIAL_STATE } from './model'
import NewAccountFields from './NewAccountFields'
import NewAccountMapPanel from './NewAccountMapPanel'
import PrintOnlyConfirmDialog from './PrintOnlyConfirmDialog'
import useNewAccountLifecycle from './useNewAccountLifecycle'
import useNewAccountMap from './useNewAccountMap'
import useNewAccountSubmit from './useNewAccountSubmit'

import type { NewAccountFormValues } from './model'

const DRAWER_WIDTH = { xs: '100vw', md: 1120 }
const FIELD_COLUMN_WIDTH = 560

type NewAccountDrawerProps = {
  isOpen?: boolean
  phone?: string
  email?: string
  onClose: () => void
}

const NewAccountDrawer = ({ isOpen = false, phone = '', email = '', onClose }: NewAccountDrawerProps) => {
  const lifecycle = useNewAccountLifecycle(isOpen)
  const { data: accountMeta } = useGetAccountMetaQuery(undefined)
  const billingProfiles = get(accountMeta, 'billingProfiles', [])
  const defaultProfile = useMemo(() => find(billingProfiles, ['default', true]) || get(billingProfiles, '[0]', null), [billingProfiles])
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false)
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const newAccountForm = useForm<NewAccountFormValues>({ defaultValues: { ...NEW_ACCOUNT_INITIAL_STATE } })
  const {
    setValue,
    reset,
    watch,
    setFocus,
    formState: { isDirty },
  } = newAccountForm
  const watchAccountName = watch('accountName')
  const watchLocationName = watch('address.addressName')
  const accountNamePastState = usePreviousValue(watchAccountName)

  const map = useNewAccountMap(setValue)
  const { submitForm, isCreating, isPrintOnlyPromptOpen, handleGoBackAndAddEmail, handleSetToPrintOnly } = useNewAccountSubmit({
    form: newAccountForm,
    lifecycle,
    isOpen,
    onClose,
    openFocusTimerRef: focusTimerRef,
  })

  // Backdrop, Escape, the header's × and Cancel all land here, so a dirty form confirms once.
  const handleRequestClose = useCallback(() => {
    if (isCreating) return
    if (isDirty) {
      setIsDiscardConfirmOpen(true)
      return
    }
    onClose()
  }, [isDirty, isCreating, onClose])

  const handleKeepEditing = useCallback(() => setIsDiscardConfirmOpen(false), [])

  const handleDiscard = useCallback(() => {
    setIsDiscardConfirmOpen(false)
    onClose()
  }, [onClose])

  useEffect(() => {
    if (!watchLocationName || accountNamePastState === watchLocationName) {
      setValue('address.addressName', watchAccountName)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the account name alone; the rest would re-run the sync.
  }, [watchAccountName])

  useEffect(() => {
    if (isOpen) {
      reset({
        ...NEW_ACCOUNT_INITIAL_STATE,
        ...(phone && { contactPhone: phone }),
        ...(email && { contactEmail: email }),
      })
      setIsDiscardConfirmOpen(false)
      map.resetMap()
      focusTimerRef.current = setTimeout(() => setFocus('accountName'), 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a reset belongs to opening, not to every prop change.
  }, [isOpen])

  // resetField is a no-op here: the dropdown is controlled, so the name never registers.
  useEffect(() => {
    if (isOpen && defaultProfile?.id) {
      setValue('billingProfileId', defaultProfile.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setValue is stable and would only widen the trigger.
  }, [isOpen, defaultProfile])

  useEffect(() => () => clearTimeout(focusTimerRef.current), [])

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        PaperProps={{
          sx: { width: DRAWER_WIDTH, maxWidth: '100vw', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 },
        }}
        sx={{ zIndex: Z_INDEX.NAVIGATION }}
        onClose={handleRequestClose}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
          <Stack
            direction="row"
            sx={{ alignItems: 'center', px: 3, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
          >
            <Typography noWrap sx={{ ...DS_TITLE_SX, flex: 1, minWidth: 0 }} variant="h4">
              {T.NEW_ACCOUNT}
            </Typography>
            <IconButton aria-label={T.CLOSE} size="small" onClick={handleRequestClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Box sx={{ display: 'flex', flex: 1, minHeight: 0, flexDirection: { xs: 'column', md: 'row' } }}>
            <NewAccountMapPanel
              control={newAccountForm.control}
              latitude={watch('address.latitude')}
              longitude={watch('address.longitude')}
              map={map}
            />
            <Box
              sx={{
                width: { xs: '100%', md: FIELD_COLUMN_WIDTH },
                flexShrink: 0,
                overflowY: 'auto',
                borderLeft: { md: '1px solid' },
                borderColor: { md: 'divider' },
                px: 3,
                py: 2,
              }}
            >
              {lifecycle.hasLoadError && (
                <HHAlert severity="error" sx={{ mb: 2 }}>
                  {T.ACCOUNT_SETTINGS_LOAD_ERROR}
                </HHAlert>
              )}
              <FormProvider {...newAccountForm}>
                <NewAccountFields
                  lifecycle={lifecycle}
                  setLatLngForMap={map.setLatLngForMap}
                  setLatLngForMapHistory={map.setLatLngForMapHistory}
                  onEnter={submitForm}
                />
              </FormProvider>
            </Box>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              justifyContent: 'flex-end',
              px: 3,
              py: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            {isDirty && (
              <Typography color="text.secondary" sx={{ ...DS_CAPTION_SX, mr: 'auto' }} variant="subtitle2">
                {T.UNSAVED_CHANGES}
              </Typography>
            )}
            <Button color="inherit" variant="text" onClick={handleRequestClose}>
              {T.CANCEL}
            </Button>
            <Button
              disableElevation
              disabled={isCreating || !lifecycle.isReady}
              loading={isCreating || (!lifecycle.isReady && !lifecycle.hasLoadError)}
              variant="contained"
              onClick={submitForm}
            >
              {T.CREATE_ACCOUNT}
            </Button>
          </Stack>
        </Box>
      </Drawer>

      <HHConfirmDialog
        cancelButtonTitle={T.KEEP_EDITING}
        confirmButtonTitle={T.DISCARD}
        confirmDescription={T.LEAVE_WITHOUT_CREATING_ACCOUNT}
        confirmTitle={T.DISCARD_CHANGES_TITLE}
        DialogProps={{ sx: { zIndex: Z_INDEX.ELEVATED_DIALOG } }}
        isOpen={isDiscardConfirmOpen}
        onClose={handleKeepEditing}
        onConfirm={handleDiscard}
      />

      <PrintOnlyConfirmDialog isOpen={isPrintOnlyPromptOpen} onGoBack={handleGoBackAndAddEmail} onSetToPrintOnly={handleSetToPrintOnly} />
    </>
  )
}

export default NewAccountDrawer
